import {
  BRANCH_MAPPING,
  BRANCH_NAME_MAPPING,
  BRANCH_NAME_SUFFIX,
} from '../constants/constants';

// Reduces the branches record to the fields the product page renders. The full
// record carries fourteen days of collection and delivery opening times per branch
// — around 16KB of serialised state — and nothing outside the branch locator reads
// them, so they are dropped rather than shipped on every page.
export const getBranchDetails = branches => {
  if (!branches || branches.length === 0) {
    return null;
  }
  return branches.reduce((details, branch) => {
    if (!branch || !branch.inventLocationId) {
      return details;
    }
    details[branch.inventLocationId] = {
      branchName: branch.branchName,
      address: branch.address,
    };
    return details;
  }, {});
};

// Name/code resolution for branches that are not in the hard-coded maps.
//
// BRANCH_MAPPING and BRANCH_NAME_MAPPING hold the legacy compound names
// ('London-Enfield', 'Birmingham-Aston') that cannot be derived from anything. New
// branches are plain — 'Croydon Branch' is reached at /product/Croydon/... — so they
// are derived from the branches file instead and need no code change to work.
//
// Registered once when the branches data loads: on the server when the blob is read,
// on the client when the branch list is fetched. Both payload shapes are accepted —
// the blob uses inventLocationId/branchName, the branch API uses code/name.
let derivedNameToCode = {};
let derivedCodeToName = {};

const lowerKeyed = source =>
  Object.keys(source).reduce((acc, key) => {
    acc[key.toLowerCase()] = source[key];
    return acc;
  }, {});

const LEGACY_NAME_TO_CODE = lowerKeyed(BRANCH_NAME_MAPPING);
const LEGACY_CODE_TO_NAME = lowerKeyed(BRANCH_MAPPING);

export const getBranchUrlName = branchName =>
  (branchName || '').replace(BRANCH_NAME_SUFFIX, '').trim();

export const registerBranches = branches => {
  const nameToCode = {};
  const codeToName = {};
  (branches || []).forEach(branch => {
    const code = branch && (branch.inventLocationId || branch.code);
    const urlName = getBranchUrlName(
      branch && (branch.branchName || branch.name)
    );
    if (!code || !urlName) {
      return;
    }
    nameToCode[urlName.toLowerCase()] = code;
    if (!codeToName[code.toUpperCase()]) {
      codeToName[code.toUpperCase()] = urlName;
    }
  });
  derivedNameToCode = nameToCode;
  derivedCodeToName = codeToName;
};

const decodeSegment = segment => {
  try {
    return decodeURIComponent(segment);
  } catch (e) {
    // A malformed escape sequence is not a branch name; match it as-is.
    return segment;
  }
};

// The two directions are deliberately asymmetric.
//
// Resolving an incoming URL checks legacy first, so every name we have ever published
// keeps working — 'London-Enfield' still takes ~35k hits a fortnight.
//
// Generating a URL prefers the derived name, so we emit one consistent name per branch
// ('Wimbledon', not 'London-Wimbledon') and agree with what the sitemap publishes. The
// compound names become inbound aliases: still accepted, never produced.
export const resolveBranchCode = branchSegment => {
  if (!branchSegment) {
    return null;
  }
  const name = decodeSegment(branchSegment).toLowerCase();
  return LEGACY_NAME_TO_CODE[name] || derivedNameToCode[name] || null;
};

export const resolveBranchName = branchCode => {
  if (!branchCode) {
    return null;
  }
  return (
    derivedCodeToName[branchCode.toUpperCase()] ||
    LEGACY_CODE_TO_NAME[branchCode.toLowerCase()] ||
    null
  );
};

// Every branch we can build a URL for, derived names winning for the same reason.
// Used to enumerate per-branch offers in the product schema.
export const getAllBranchUrlNames = () => ({
  ...BRANCH_MAPPING,
  ...derivedCodeToName,
});
